/**
 * HALO - Login Page
 * Clean, centered authentication with privacy-first messaging
 * Design: Centered card on subtle gradient background
 */

import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { supabase } from "../lib/supabase";
import {
  AlertCircle,
  User,
  Shield,
  Lock,
  Mail,
  ArrowRight,
  Cpu,
  CheckCircle2,
  Building,
} from "lucide-react";
import {
  Card,
  CardContent,
} from "@/components/ui/card";

type AuthMode = "signin" | "signup";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [mode, setMode] = useState<AuthMode>("signin");
  const [success, setSuccess] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setSuccess(null);

    try {
      if (mode === "signup") {
        const { error } = await supabase.auth.signUp({
          email,
          password,
          options: {
            data: {
              full_name: fullName || email.split("@")[0],
            },
          },
        });
        if (error) throw error;
        setSuccess("Account created! Please check your email to confirm your account.");
      } else {
        const { error } = await supabase.auth.signInWithPassword({
          email,
          password,
        });
        if (error) throw error;
      }
    } catch (error: any) {
      let message = error.message || "Authentication failed";
      if (message.includes("Invalid login credentials")) {
        message = "Invalid email or password. Please check your credentials.";
      } else if (message.includes("Email not confirmed")) {
        message = "Please check your email to confirm your account.";
      }
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-halo px-4 relative overflow-hidden">
      <div className="absolute inset-0 bg-grid-pattern opacity-20 pointer-events-none" />

      <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-96 h-96 bg-primary/20 rounded-full blur-3xl pointer-events-none" />

      <motion.div
        className="w-full max-w-md relative z-10"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
      >
        <div className="text-center mb-8">
          <motion.div
            className="inline-flex items-center justify-center mb-6"
            whileHover={{ scale: 1.02 }}
            transition={{ duration: 0.2 }}
          >
            <img src="/haloai_logo.webp" alt="HALO Logo" className="w-16 h-16 object-contain" />
          </motion.div>
          <motion.h1
            className="text-3xl font-semibold text-foreground tracking-tight mb-2"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1 }}
          >
            HALO
          </motion.h1>
          <motion.p
            className="text-muted-slate"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
          >
            Employee Wellness Monitoring Platform
          </motion.p>
        </div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2, duration: 0.4 }}
        >
          <Card>
            <CardContent className="pt-6">
              <div className="flex p-1 mb-6 bg-surface-dark rounded-xl border border-glass-grey">
                <button
                  onClick={() => {
                    setMode("signin");
                    setError("");
                    setSuccess(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-micro ${
                    mode === "signin"
                      ? "bg-surface-elevated text-foreground shadow-sm"
                      : "text-muted-slate hover:text-foreground"
                  }`}
                >
                  <User className="w-4 h-4" />
                  Sign In
                </button>
                <button
                  onClick={() => {
                    setMode("signup");
                    setError("");
                    setSuccess(null);
                  }}
                  className={`flex-1 flex items-center justify-center gap-2 py-2.5 rounded-lg text-sm font-medium transition-all duration-micro ${
                    mode === "signup"
                      ? "bg-surface-elevated text-foreground shadow-sm"
                      : "text-muted-slate hover:text-foreground"
                  }`}
                >
                  <Building className="w-4 h-4" />
                  Sign Up
                </button>
              </div>

              <AnimatePresence mode="wait">
                {error && (
                  <motion.div
                    className="mb-4 p-3 rounded-xl bg-signal-crimson/10 border border-signal-crimson/20 flex items-start gap-3"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <AlertCircle className="w-5 h-5 text-signal-crimson flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-signal-crimson">{error}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <AnimatePresence mode="wait">
                {success && (
                  <motion.div
                    className="mb-4 p-3 rounded-xl bg-signal-green/10 border border-signal-green/20 flex items-start gap-3"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: "auto" }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    <CheckCircle2 className="w-5 h-5 text-signal-green flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-signal-green">{success}</p>
                  </motion.div>
                )}
              </AnimatePresence>

              <form onSubmit={handleSubmit} className="space-y-4">
                <AnimatePresence mode="wait">
                  {mode === "signup" && (
                    <motion.div
                      key="fullname"
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: "auto" }}
                      exit={{ opacity: 0, height: 0 }}
                    >
                      <label className="block text-sm font-medium text-muted-slate mb-2">
                        Your Name
                      </label>
                      <div className="relative">
                        <User className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                        <input
                          type="text"
                          value={fullName}
                          onChange={(e) => setFullName(e.target.value)}
                          className="halo-input pl-11"
                          placeholder="John Doe"
                        />
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Email Address
                  </label>
                  <div className="relative">
                    <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                    <input
                      type="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      required
                      className="halo-input pl-11"
                      placeholder="you@company.com"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-muted-slate mb-2">
                    Password
                  </label>
                  <div className="relative">
                    <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-slate" />
                    <input
                      type="password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      required
                      minLength={6}
                      className="halo-input pl-11"
                      placeholder="••••••••"
                    />
                  </div>
                </div>

                <motion.button
                  type="submit"
                  disabled={loading}
                  className="w-full halo-btn halo-btn--primary mt-6"
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle
                          className="opacity-25"
                          cx="12"
                          cy="12"
                          r="10"
                          stroke="currentColor"
                          strokeWidth="4"
                          fill="none"
                        />
                        <path
                          className="opacity-75"
                          fill="currentColor"
                          d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                        />
                      </svg>
                      {mode === "signup" ? "Creating Account..." : "Signing in..."}
                    </span>
                  ) : (
                    <span className="flex items-center justify-center gap-2">
                      {mode === "signup" ? "Create Account" : "Sign In"}
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  )}
                </motion.button>
              </form>

              <div className="mt-6 pt-6 border-t border-glass-grey">
                <div className="flex items-center justify-center gap-6 text-xs text-muted-slate">
                  <div className="flex items-center gap-1.5">
                    <Cpu className="w-3.5 h-3.5 text-shield" />
                    <span>Processed locally</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <Shield className="w-3.5 h-3.5 text-shield" />
                    <span>End-to-end encrypted</span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>

        <motion.p
          className="text-center text-xs text-muted-slate mt-6"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4 }}
        >
          Privacy-first employee wellness monitoring
        </motion.p>
      </motion.div>
    </div>
  );
}
